import { useState, useCallback, useMemo } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Image,
  Modal,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  useWindowDimensions,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Check, BookOpen } from "lucide-react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@louise/moon-ui/primitives/Accordion";
import { useReadArticles } from "@louise/favorites";
import {
  useScrollHaptics,
  springs,
  haptic,
  SectionTitle,
} from "@louise/moon-ui";
import { getBlock } from "@louise/content";
import type { ArticleBody, ContentBlock, ContentTag } from "@louise/content";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { EMBER } from "../../onboarding/theme";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { ArticleRailCard, type Article } from "./ArticleSectionCard";
import { ArticleReaderActions } from "./ArticleReaderActions";
import { ChatAboutArticleButton } from "./ChatAboutArticleButton";
import { useArticleBookmark } from "../hooks/useArticleBookmark";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import {
  QuickSummaryCard,
  EmberBullet,
  ReferencesPanel,
  DiagramPlaceholder,
  VideoEmbed,
} from "./article";

type Props = {
  article: Article | null;
  onClose: () => void;
  onOpenArticle?: (article: Article) => void;
  onChatAboutArticle?: () => void;
  sectionArticles?: Article[];
};

const TAG_I18N_KEYS: Partial<Record<ContentTag, string>> = {
  "moon-guides": "guides",
  "moon-decisions": "decisions",
  "moon-science": "science",
  "moon-costs": "costs",
  endo: "endometriosis",
  pcos: "pcos",
  thyroid: "thyroid",
  "clinic-visit": "clinic",
  "during-cycle": "treatment",
  "pre-cycle": "preparation",
  "egg-freezing": "egg_freezing",
  lifestyle: "lifestyle",
  optimization: "optimization",
  emotional: "emotional_support",
  testing: "testing",
  practical: "practical",
  "both-partners": "partners",
  his: "male_fertility",
  her: "female_fertility",
  clinic: "clinic",
};

/** Threshold (px) after which in-card buttons are off-screen */
const HEADER_ACTIONS_THRESHOLD = 60;

export function buildArticleChatPrompt(
  article: Article,
  t: TranslationFunction,
): string {
  const quickSummary = article.body.quickSummary
    ?.map((item) => `${item.title}: ${item.description}`)
    .join("\n");

  return [
    t("learn.article_chat_prompt.just_read_article"),
    t("learn.article_chat_prompt.article_line", {
      params: { title: article.title },
    }),
    t("learn.article_chat_prompt.summary_line", {
      params: {
        teaser: article.teaser || t("learn.article_chat_prompt.no_summary"),
      },
    }),
    quickSummary
      ? `${t("learn.article_chat_prompt.key_takeaways_heading")}\n${quickSummary}`
      : null,
    t("learn.article_chat_prompt.help_me_understand"),
  ]
    .filter((line): line is string => Boolean(line))
    .join("\n");
}

function deriveRelatedArticleTag(
  block: ContentBlock<"article">,
  t: TranslationFunction,
): string {
  const body = block.body as ArticleBody;
  if (
    body.introduction &&
    body.introduction.length <= 30 &&
    !body.introduction.includes(".")
  ) {
    return body.introduction;
  }

  for (const tag of block.tags) {
    const key = TAG_I18N_KEYS[tag];
    if (key) return t(`learn.article_tags.${key}`);
  }

  return "";
}

function deriveRelatedArticleTeaser(block: ContentBlock<"article">): string {
  if (block.subtitle && block.subtitle.length <= 80) {
    return block.subtitle;
  }
  if (block.subtitle) {
    return block.subtitle.slice(0, 77) + "...";
  }

  const body = block.body as ArticleBody;
  const firstSection = body.sections[0]?.content;
  if (!firstSection) return "";
  return firstSection.length <= 80
    ? firstSection
    : firstSection.slice(0, 77) + "...";
}

function relatedBlockToArticle(
  block: ContentBlock<"article">,
  t: TranslationFunction,
): Article {
  const body = block.body as ArticleBody;
  return {
    id: block.id,
    title: block.title,
    time: block.readTimeMinutes
      ? t("learn.read_time_minutes", {
          params: { minutes: String(block.readTimeMinutes) },
        })
      : "",
    tag: deriveRelatedArticleTag(block, t),
    teaser: deriveRelatedArticleTeaser(block),
    body,
    tags: block.tags,
  };
}

export function ArticleReaderContent({
  article,
  onClose,
  onOpenArticle,
  onChatAboutArticle,
  sectionArticles,
  skipExitAnimation,
}: {
  article: Article;
  onClose: () => void;
  onOpenArticle?: (article: Article) => void;
  onChatAboutArticle?: () => void;
  sectionArticles?: Article[];
  skipExitAnimation?: boolean;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("ArticleReaderContent");
  const insets = useSafeAreaInsets();
  const { width: screenWidth } = useWindowDimensions();
  const cardMaxWidth = screenWidth * 0.43;
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);
  const [scrollPercent, setScrollPercent] = useState(0);
  const [showHeaderActions, setShowHeaderActions] = useState(false);
  const { isRead } = useReadArticles();
  const { bookmarked, toggleBookmark } = useArticleBookmark(article.id);
  const articleActionLabels = {
    savedLabel: t("learn.article_actions.saved"),
    saveAccessibilityLabel: t("learn.article_actions.save_accessibility"),
    removeAccessibilityLabel: t(
      "learn.article_actions.remove_saved_accessibility",
    ),
    closeAccessibilityLabel: t("learn.article_actions.close_accessibility"),
  };
  const onScrollHaptics = useScrollHaptics();

  useState(() => {
    scale.value = withSpring(1, springs.sheet);
    opacity.value = withTiming(1, { duration: 250 });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleClose = useCallback(() => {
    if (skipExitAnimation) {
      onClose();
      return;
    }
    scale.value = withSpring(0.92, springs.sheet);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(onClose)();
    });
  }, [scale, opacity, onClose, skipExitAnimation]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScrollHaptics(e);
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const scrollable = contentSize.height - layoutMeasurement.height;
      if (scrollable <= 0) {
        setScrollPercent(100);
        return;
      }
      const pct = Math.max(
        0,
        Math.min(100, Math.round((contentOffset.y / scrollable) * 100)),
      );
      setScrollPercent(pct);
      setShowHeaderActions(contentOffset.y > HEADER_ACTIONS_THRESHOLD);
    },
    [onScrollHaptics],
  );

  const { body } = article;
  const hasQuickSummary = body.quickSummary && body.quickSummary.length > 0;
  const hasReferences = body.references && body.references.length > 0;
  const relatedArticles = useMemo(() => {
    if (!body.relatedArticles?.length) return [];
    return body.relatedArticles
      .map((relId) => {
        const block = getBlock(relId);
        if (!block || block.kind !== "article") return null;
        return relatedBlockToArticle(block as ContentBlock<"article">, t);
      })
      .filter((relArticle): relArticle is Article => !!relArticle);
  }, [body.relatedArticles, t]);
  const relatedArticleRail =
    relatedArticles.length > 0 && onOpenArticle ? (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          gap: 12,
          paddingLeft: 24,
          paddingRight: 32,
        }}
      >
        {relatedArticles.map((relArticle) => (
          <ArticleRailCard
            key={relArticle.id}
            article={relArticle}
            read={isRead(relArticle.id)}
            onPress={() => onOpenArticle(relArticle)}
            action="related_article_row"
            cardMaxWidth={cardMaxWidth}
          />
        ))}
      </ScrollView>
    ) : null;

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />

      <Animated.View style={animatedStyle}>
        <View style={{ flex: 1, paddingTop: insets.top }}>
          {/* Fixed header: progress bar + actions (actions appear on scroll) */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              paddingHorizontal: 16,
            }}
          >
            <View
              style={{
                flex: 1,
                height: 3,
                backgroundColor: "rgba(0,0,0,0.04)",
                borderRadius: 2,
              }}
            >
              <View
                style={{
                  height: 3,
                  width: `${scrollPercent}%`,
                  backgroundColor: EMBER.colors.warm,
                  borderRadius: 2,
                }}
              />
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 10,
                fontWeight: "600",
                color: EMBER.colors.textFaint,
                minWidth: 28,
                textAlign: "right",
              }}
            >
              {scrollPercent}%
            </Text>
            {showHeaderActions && (
              <ArticleReaderActions
                bookmarked={bookmarked}
                onToggleFavorite={toggleBookmark}
                onClose={handleClose}
                size={28}
                {...articleActionLabels}
              />
            )}
          </View>

          <Animated.ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            <View
              style={{
                ...GLASS_CARD_STYLE,
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              {/* In-card actions (top-right) */}
              <View
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  flexDirection: "row",
                  gap: 6,
                  zIndex: 1,
                }}
              >
                <ArticleReaderActions
                  bookmarked={bookmarked}
                  onToggleFavorite={toggleBookmark}
                  onClose={handleClose}
                  size={32}
                  {...articleActionLabels}
                />
              </View>

              {/* Tag + contextual chat entry point */}
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                {article.tag ? (
                  <View
                    style={{
                      paddingHorizontal: 10,
                      paddingVertical: 4,
                      borderRadius: 999,
                      backgroundColor: `${EMBER.colors.warm}14`,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 11,
                        fontWeight: "600",
                        color: EMBER.colors.warm,
                        letterSpacing: 0.3,
                      }}
                    >
                      {article.tag}
                    </Text>
                  </View>
                ) : null}
                {onChatAboutArticle ? (
                  <ChatAboutArticleButton
                    label={t("learn.article_actions.chat_about_this")}
                    accessibilityLabel={t(
                      "learn.article_actions.chat_about_this_accessibility",
                    )}
                    onPress={onChatAboutArticle}
                  />
                ) : null}
              </View>

              {/* Title */}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 24,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                  lineHeight: 30,
                  marginBottom: 10,
                  paddingRight: 126,
                }}
              >
                {article.title}
              </Text>

              {/* Teaser */}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 15,
                  color: EMBER.colors.textLight,
                  lineHeight: 21,
                  marginBottom: 24,
                  fontStyle: "italic",
                }}
              >
                {article.teaser}
              </Text>

              {/* Quick Summary */}
              {hasQuickSummary && (
                <QuickSummaryCard items={body.quickSummary!} />
              )}

              {/* Accordion Sections */}
              <Accordion
                type="multiple"
                defaultValue={body.sections.map((_, i) => `section-${i}`)}
                collapsible
              >
                {body.sections.map((section, i) => (
                  <AccordionItem
                    key={i}
                    value={`section-${i}`}
                    className="border-b-0"
                    style={{ marginBottom: 16 }}
                  >
                    <AccordionTrigger className="py-3">
                      <View style={{ flex: 1, paddingRight: 8 }}>
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 16,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                            lineHeight: 22,
                          }}
                        >
                          {section.title}
                        </Text>
                        {section.subtitle && (
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 12,
                              color: EMBER.colors.textLight,
                              fontStyle: "italic",
                              marginTop: 2,
                            }}
                          >
                            {section.subtitle}
                          </Text>
                        )}
                      </View>
                    </AccordionTrigger>
                    <AccordionContent>
                      {section.content && (
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 15,
                            color: EMBER.colors.textMid,
                            lineHeight: 24,
                            marginBottom:
                              section.bulletPoints &&
                              section.bulletPoints.length > 0
                                ? 12
                                : 0,
                          }}
                        >
                          {section.content}
                        </Text>
                      )}
                      {section.bulletPoints?.map((point, j) => (
                        <View
                          key={j}
                          style={{
                            flexDirection: "row",
                            gap: 10,
                            alignItems: "flex-start",
                            marginBottom:
                              j < (section.bulletPoints?.length ?? 0) - 1
                                ? 8
                                : 0,
                          }}
                        >
                          <EmberBullet />
                          <Text
                            className="font-dm-sans"
                            style={{
                              flex: 1,
                              fontSize: 14,
                              color: EMBER.colors.textMid,
                              lineHeight: 21,
                            }}
                          >
                            {point}
                          </Text>
                        </View>
                      ))}
                      {section.diagram && (
                        <DiagramPlaceholder
                          label={section.diagram.label}
                          description={section.diagram.description}
                        />
                      )}
                      {section.media && (
                        <VideoEmbed
                          caption={section.media.caption}
                          url={section.media.url}
                        />
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {/* References */}
              {hasReferences && (
                <ReferencesPanel references={body.references!} />
              )}

              {/* Related Articles */}
              {relatedArticleRail && (
                <View style={{ marginTop: 24 }}>
                  <View style={{ marginBottom: 12 }}>
                    <SectionTitle icon={BookOpen}>
                      {t("learn.article_reader.related_articles")}
                    </SectionTitle>
                  </View>
                  <View style={{ marginHorizontal: -24 }}>
                    {Platform.OS === "web" ? (
                      <View
                        style={
                          {
                            WebkitMaskImage:
                              "linear-gradient(to right, black 90%, transparent 100%)",
                            maskImage:
                              "linear-gradient(to right, black 90%, transparent 100%)",
                          } as Record<string, unknown>
                        }
                      >
                        {relatedArticleRail}
                      </View>
                    ) : (
                      relatedArticleRail
                    )}
                  </View>
                </View>
              )}
            </View>

            {/* Continue reading — same cards as ArticleSectionCard */}
            {sectionArticles && sectionArticles.length > 0 && onOpenArticle && (
              <View style={{ marginTop: 16 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    color: EMBER.colors.textFaint,
                    fontWeight: "600",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    marginBottom: 10,
                  }}
                >
                  {t("learn.article_reader.continue_reading")}
                </Text>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ gap: 12 }}
                >
                  {sectionArticles.map((a) => {
                    const read = isRead(a.id);
                    return (
                      <Pressable
                        key={a.id}
                        onPress={() => {
                          capturePress(
                            pressLabel(a.title),
                            "continue_reading_article",
                          );
                          haptic("light");
                          onOpenArticle(a);
                        }}
                        style={{
                          width: 180,
                          height: 220,
                          borderRadius: 20,
                          backgroundColor: "rgba(255,255,255,0.45)",
                          borderWidth: 1,
                          borderColor: "rgba(255,255,255,0.6)",
                          padding: 18,
                          justifyContent: "space-between",
                        }}
                      >
                        {/* Top: tag + read indicator */}
                        <View
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <View style={{ flex: 1 }}>
                            <Text
                              className="font-dm-sans"
                              numberOfLines={1}
                              style={{
                                fontSize: 10,
                                fontWeight: "600",
                                color: EMBER.colors.textFaint,
                                letterSpacing: 0.5,
                                textTransform: "uppercase",
                              }}
                            >
                              {a.tag}
                            </Text>
                            {a.time ? (
                              <Text
                                className="font-dm-sans"
                                style={{
                                  fontSize: 10,
                                  color: EMBER.colors.textFaint,
                                  marginTop: 2,
                                }}
                              >
                                {a.time}
                              </Text>
                            ) : null}
                          </View>
                          {read && (
                            <View
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 10,
                                backgroundColor: `${EMBER.colors.green}14`,
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Check
                                size={11}
                                color={EMBER.colors.green}
                                strokeWidth={2}
                              />
                            </View>
                          )}
                        </View>

                        {/* Bottom: title + teaser */}
                        <View style={{ gap: 6 }}>
                          <Text
                            className="font-dm-sans"
                            numberOfLines={3}
                            style={{
                              fontSize: 15,
                              fontWeight: "700",
                              color: EMBER.colors.text,
                              lineHeight: 20,
                            }}
                          >
                            {a.title}
                          </Text>
                          <Text
                            className="font-dm-sans"
                            numberOfLines={2}
                            style={{
                              fontSize: 12,
                              color: EMBER.colors.textLight,
                              lineHeight: 17,
                            }}
                          >
                            {a.teaser}
                          </Text>
                        </View>
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>
            )}
          </Animated.ScrollView>
        </View>
      </Animated.View>
    </View>
  );
}

export default function ArticleReaderSheet({
  article,
  onClose,
  onOpenArticle,
  onChatAboutArticle,
  sectionArticles,
}: Props) {
  if (!article) return null;

  return (
    <Modal visible transparent animationType="none" onRequestClose={onClose}>
      <ArticleReaderContent
        article={article}
        onClose={onClose}
        onOpenArticle={onOpenArticle}
        onChatAboutArticle={onChatAboutArticle}
        sectionArticles={sectionArticles}
      />
    </Modal>
  );
}
